import { Button, Icon } from '@glade-ai/ui';
import { type FC } from 'react';

import { type MediaApiModels } from '@/tsClient/servers/noodleMedia';
import MimeTypes from '@/typings/MimeTypes';

type Props = {
  file: Pick<MediaApiModels.Media, 'id' | 'url' | 'type'> & {
    name?: string;
  };
};

const getFileMetadata = (mimeType: string): { icon: string; extension: string } => {
  switch (mimeType) {
    case MimeTypes.PDF:
      return { extension: 'pdf', icon: 'ph--file-pdf--fill' };
    case MimeTypes.CSV:
      return { extension: 'csv', icon: 'ph--file-csv--fill' };
    case MimeTypes.ZIP:
      return { extension: 'zip', icon: 'ph--file-zip--fill' };
    case MimeTypes.DOC:
    case MimeTypes.DOCX:
      return { extension: mimeType === MimeTypes.DOC ? 'doc' : 'docx', icon: 'ph--file-doc--fill' };
    case MimeTypes.TXT:
      return { extension: 'txt', icon: 'ph--file-text--fill' };
    case MimeTypes.PPT:
    case MimeTypes.PPTX:
      return { extension: mimeType === MimeTypes.PPT ? 'ppt' : 'pptx', icon: 'ph--file-ppt--fill' };
    case MimeTypes.XLS:
    case MimeTypes.XLSX:
      return { extension: mimeType === MimeTypes.XLS ? 'xls' : 'xlsx', icon: 'ph--file-xls--fill' };
    case MimeTypes.AVI:
      return { extension: 'avi', icon: 'ph--file--fill' };
    default:
      return { extension: 'bin', icon: 'ph--file--fill' };
  }
};

const download = async (url: string, name: string): Promise<void> => {
  if (!url) {
    console.warn('Download URL is empty');
    return;
  }

  let blobURL: string | null = null;
  let a: HTMLAnchorElement | null = null;

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    blobURL = URL.createObjectURL(blob);
    a = document.createElement('a');
    a.href = blobURL;
    a.style.display = 'none';

    if (name) a.download = name;
    document.body.appendChild(a);
    a.click();
  } finally {
    // Clean up DOM element and object URL
    if (a) {
      document.body.removeChild(a);
    }
    if (blobURL) {
      URL.revokeObjectURL(blobURL);
    }
  }
};

const RenderFile: FC<Props> = ({ file }) => {
  const { icon, extension } = getFileMetadata(file.type || '');
  return (
    <Button
      size='lg'
      className='h-auto w-full py-2 min-w-0'
      onClick={() => download(file.url || '', file.name || (file.type ? `${file.id}.${extension}` : file.id))}
    >
      <Icon icon={icon} size={6} className='text-[var(--color-primary)]' />
      <div className='min-w-0 flex-1'>
        <p className='truncate'>{file.name || 'Click to download file'}</p>
        {file.name && <span className='gl-description'>Click to download</span>}
      </div>
    </Button>
  );
};

export default RenderFile;
